/**
 * Line-level diff rows for prompt text, shared by the Review draft page and
 * prompt suggestion cards on Eval Results (PROC-265).
 */
// `diff` (jsdiff) is imported as a transitive dep — several devtools
// deps pull it. If we start depending on this more heavily, promote it
// to a direct dep in package.json so we own the version pin.
import { diffLines } from "diff";

export const DiffLineKind = {
  ADDED: "added",
  REMOVED: "removed",
  CONTEXT: "context",
  /** A folded run of unchanged lines. */
  SKIPPED: "skipped",
} as const;

export interface DiffLineRow {
  key: string;
  line: string;
  kind: typeof DiffLineKind.ADDED | typeof DiffLineKind.REMOVED | typeof DiffLineKind.CONTEXT;
}

export interface SkippedLinesRow {
  key: string;
  kind: typeof DiffLineKind.SKIPPED;
  count: number;
}

export type DiffDisplayRow = DiffLineRow | SkippedLinesRow;

/** Every line ends in a newline, the end of the text included: jsdiff
 *  compares lines with their newline, so a last line without one would
 *  diff as removed and re-added against the same line with one. */
const normalize = (text: string): string => {
  const trimmed = (text || "").trimEnd();
  return trimmed ? `${trimmed}\n` : "";
};

/** Compute a line-level unified diff. Splits each `diffLines` chunk into
 *  actual rows so we can render each line with its own prefix + colour
 *  (matches GitHub PR-diff feel). Trailing empty lines from `.split("\n")`
 *  get dropped since they're an artifact of the split, not content. */
export function diffLineRows(before: string, after: string): DiffLineRow[] {
  const rows: DiffLineRow[] = [];
  diffLines(normalize(before), normalize(after)).forEach((change, ci) => {
    const raw = change.value.split("\n");
    if (raw.length > 0 && raw[raw.length - 1] === "") raw.pop();
    raw.forEach((line, li) => {
      rows.push({
        key: `${ci}-${li}`,
        line,
        kind: change.added ? DiffLineKind.ADDED : change.removed ? DiffLineKind.REMOVED : DiffLineKind.CONTEXT,
      });
    });
  });
  return rows;
}

/** Keeps `context` unchanged lines either side of each change and folds the
 *  rest into one row per run. A run of one line stays as the line. With no
 *  change at all, every row is kept. */
export function foldUnchanged(rows: DiffLineRow[], context = 2): DiffDisplayRow[] {
  const changed = rows.map((row) => row.kind !== DiffLineKind.CONTEXT);
  if (!changed.some(Boolean)) return rows;
  const near = (i: number) => changed.slice(Math.max(0, i - context), i + context + 1).some(Boolean);

  const out: DiffDisplayRow[] = [];
  let run: DiffLineRow[] = [];
  const flush = () => {
    if (run.length > 1) out.push({ key: `skipped-${run[0].key}`, kind: DiffLineKind.SKIPPED, count: run.length });
    else out.push(...run);
    run = [];
  };
  rows.forEach((row, i) => {
    if (near(i)) {
      flush();
      out.push(row);
    } else {
      run.push(row);
    }
  });
  flush();
  return out;
}
